import { useState } from 'react';
import { useApiClient } from '@/lib/api/hooks';
import type { paths } from '@/lib/api/types.gen';
import type { ApiErrorBody, QuoteError } from './_shared';

type RecoveryBody = NonNullable<
  paths['/calc/recovery-options']['post']['requestBody']
>['content']['application/json'];
type RecoveryInput = RecoveryBody['input'];

export type RecoveryPatch = {
  sxl?: number;
  xxl?: number;
  xxxl?: number;
  xxxxl?: number;
  xxxxxl?: number;
  discountPct?: number;
  frontEnabled?: boolean;
  frontColors?: number;
  backEnabled?: boolean;
  backColors?: number;
  sleeveEnabled?: boolean;
  sleeveColors?: number;
  garmentSource?: 'we_provide_by_style';
  style?: string;
  colorName?: string;
};

export type RecoveryOption = {
  label: string;
  detail: string;
  kind: 'discount' | 'location' | 'qty' | 'garment';
  patch: RecoveryPatch;
  perUnitSell: number;
  orderTotal: number;
  marginPct: number;
  marginDelta: number;
};

export type RecoveryResult = {
  baseline: { perUnitSell: number; marginPct: number; orderTotal: number } | null;
  options: RecoveryOption[];
  summary: string;
};

export type RecoveryState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; result: RecoveryResult }
  | { status: 'error'; error: QuoteError };

export function useRecoveryOptions() {
  const client = useApiClient();
  const [state, setState] = useState<RecoveryState>({ status: 'idle' });

  async function run(
    input: RecoveryInput,
    colorName?: string,
    category?: 'tee' | 'hoodie',
  ): Promise<RecoveryResult | null> {
    setState({ status: 'loading' });
    try {
      const { data, error, response } = await client.POST('/calc/recovery-options', {
        body: { input, colorName, category, targetMarginPct: 40, limit: 4 },
      });
      if (error || !data) {
        const envelope = error as { error?: ApiErrorBody } | undefined;
        const quoteError: QuoteError = {
          status: response.status,
          body: envelope?.error ?? {
            type: 'unknown',
            title: 'Unknown error',
            detail: `HTTP ${response.status}`,
          },
        };
        setState({ status: 'error', error: quoteError });
        return null;
      }
      setState({ status: 'success', result: data.data });
      return data.data;
    } catch (err) {
      const quoteError: QuoteError = {
        status: 0,
        body: {
          type: 'network_error',
          title: 'Could not reach the API',
          detail: err instanceof Error ? err.message : 'Request failed before reaching the server.',
        },
      };
      setState({ status: 'error', error: quoteError });
      return null;
    }
  }

  function reset() {
    setState({ status: 'idle' });
  }

  return { state, run, reset };
}
